/**
 * Promo Display Delay Hook
 * 
 * Manages the 5-second delay for first-time promo display (popup or button).
 * Follows Single Responsibility Principle - only handles delay logic.
 * 
 * Key Issue Fixed: Prevents display state from being lost when markFirstDisplayComplete
 * transitions first_time_visible from true to false during or after delay completion.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { FIRST_SHOW_DELAY } from '../utils/promoConstants';
export var usePromoDisplayDelay = function usePromoDisplayDelay(_ref) {
  var isEligible = _ref.isEligible,
    isFirstTime = _ref.isFirstTime,
    onFirstDisplayComplete = _ref.onFirstDisplayComplete;
  var _useState = useState(false),
    shouldShowWithDelay = _useState[0],
    setShouldShowWithDelay = _useState[1];
  var _useState2 = useState(false),
    isDelayActive = _useState2[0],
    setIsDelayActive = _useState2[1];
  var _useState3 = useState(0),
    remainingDelay = _useState3[0],
    setRemainingDelay = _useState3[1];
  var delayTimeoutRef = useRef(null);
  var countdownIntervalRef = useRef(null);
  var hasTriggeredFirstDisplayRef = useRef(false);

  // Clear all timers
  var clearTimers = useCallback(function () {
    if (delayTimeoutRef.current) {
      clearTimeout(delayTimeoutRef.current);
      delayTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Start delay countdown
  var startDelayCountdown = useCallback(function () {
    setRemainingDelay(FIRST_SHOW_DELAY);
    setIsDelayActive(true);

    // Main delay timer
    delayTimeoutRef.current = setTimeout(function () {
      setShouldShowWithDelay(true);
      setIsDelayActive(false);
      setRemainingDelay(0);
      clearTimers();

      // Mark first display as complete AFTER setting shouldShowWithDelay
      hasTriggeredFirstDisplayRef.current = true;
      // Delay the state update to avoid immediate re-render conflicts
      onFirstDisplayComplete === null || onFirstDisplayComplete === void 0 ? void 0 : onFirstDisplayComplete();
    }, FIRST_SHOW_DELAY);
  }, [onFirstDisplayComplete, clearTimers]);

  // Reset delay state
  var resetDelayState = useCallback(function () {
    clearTimers();
    setShouldShowWithDelay(false);
    setIsDelayActive(false);
    setRemainingDelay(0);
    hasTriggeredFirstDisplayRef.current = false;
  }, [clearTimers]);

  // Main effect - trigger delay logic
  useEffect(function () {
    // Reset if not eligible
    if (!isEligible) {
      resetDelayState();
      return;
    }

    // If first time and not already triggered, start delay
    if (isFirstTime && !hasTriggeredFirstDisplayRef.current && !isDelayActive) {
      startDelayCountdown();
    }

    // If not first time, show immediately (unless we're already showing or in delay)
    if (!isFirstTime && !hasTriggeredFirstDisplayRef.current && !isDelayActive) {
      setShouldShowWithDelay(true);
      hasTriggeredFirstDisplayRef.current = true;
    }

    // Handle transition from first-time to non-first-time (after markFirstDisplayComplete)
    // This ensures we maintain the display state when transitioning
    if (!isFirstTime && hasTriggeredFirstDisplayRef.current && !shouldShowWithDelay && !isDelayActive) {
      setShouldShowWithDelay(true);
    }
  }, [isEligible, isFirstTime, isDelayActive, shouldShowWithDelay, startDelayCountdown, resetDelayState]);

  // Cleanup on unmount
  useEffect(function () {
    return function () {
      clearTimers();
    };
  }, [clearTimers]);

  // Reset when eligibility changes from true to false
  useEffect(function () {
    if (!isEligible && hasTriggeredFirstDisplayRef.current) {
      hasTriggeredFirstDisplayRef.current = false;
    }
  }, [isEligible]);
  return {
    shouldShowWithDelay: shouldShowWithDelay,
    isDelayActive: isDelayActive,
    remainingDelay: remainingDelay
  };
};